import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  FormGroup,
  FormControlLabel,
  IconButton,
  Input,
  InputAdornment,
  Switch,
  Tooltip,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  EditOutlined,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from "@mui/icons-material";
import { debounce } from "lodash";
import Fade from "@mui/material/Fade";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import { Loader } from "../../components/CustomLoader";
import { renderEllipsisText } from "../../utils/cssStyles";
import CustomPagination from "../../components/CustomPagination";
import {
  ACCOUNT_TYPE_OPTIONS,
  KYC_STATUS_OPTIONS,
  ROLE_OPTIONS_LIST,
  STATUS_OPTIONS_LIST,
} from "../../utils/constants";
import { capitalize } from "../../utils/helpers";
import { useBulkSelect } from "../../hooks/useBulkSelect";

export default function UserManagementList() {
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [role, setRole] = useState([]);
  const [kycStatus, setKycStatus] = useState([]);
  const [accountType, setAccountType] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    id: "",
    currentStatus: 0,
  });
  const [blockDialog, setBlockDialog] = useState({
    open: false,
    id: "",
    isBlocked: false,
  });
  const [isArchivedView, setIsArchivedView] = useState(false);

  const {
    selectedRows,
    getCheckboxColumn,
    clearSelection,
  } = useBulkSelect(data, null, [currentPage, pageSize, status, refresh, role, kycStatus, accountType, isArchivedView]);

  const fetchUsers = async (query = "") => {
    try {
      setIsLoading(true);
      const body = {
        searchItem: query,
        pageNumber: currentPage,
        pageSize,
        status: isArchivedView ? [3] : [0, 1],
        role: accountType,
        kycStatus,
        isArchived: isArchivedView,
      };
      const res = await userServices.getUser(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.result || []);
          setTotalPages(res?.data?.data?.totalRecords);
        }
        setLoaderDelay(true);
        setIsLoading(false);
      }, 400);
    } catch (err) {
      setData([]);
      errorHandler(err);
      setIsLoading(false);
    } 
  };

  const debouncedSearch = useCallback(debounce(fetchUsers, 400), [
    currentPage,
    pageSize,
    status,
    role,
    kycStatus,
    accountType,
    isArchivedView,
  ]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const deleteUser = async (ids) => {
    const isMultiple = Array.isArray(ids);
    const result = await Swal.fire({
      text: isMultiple 
        ? `Are you sure you want to delete ${ids.length} selected user(s)?`
        : "Are you sure you want to delete this user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });

    if (result.isConfirmed) {
      try {
        const userIds = isMultiple ? ids : [ids];
        const payload = {
          userId: userIds.length === 1 ? userIds[0] : userIds,
        };
        const res = await userServices.deleteUser(payload);
        if (res?.status === 200) {
          toast.success(res.data.message);
          clearSelection();
          setRefresh(!refresh);
        } else {
          toast.error(res.data.message);
        }
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const handleBulkDelete = () => {
    if (selectedRows.length > 0) {
      deleteUser(selectedRows);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedRows.length === 0) return;
    
    const result = await Swal.fire({
      text: `Are you sure you want to archive ${selectedRows.length} selected user(s)?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Archive",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ff9800",
    });

    if (result.isConfirmed) {
      try {
        const payload = selectedRows.length === 1 
          ? { id: selectedRows[0], status: 3 }
          : { id: selectedRows, status: 3 };
        
        const res = await userServices.changeStatus(payload);
        if (res?.status === 200) {
          toast.success(res.data.message || `${selectedRows.length} user(s) archived successfully`);
          clearSelection();
          setRefresh(!refresh);
        } else {
          toast.error(res.data.message || "Failed to archive users");
        }
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const toggleStatus = async () => {
    const { id, currentStatus } = statusDialog;
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await userServices.changeStatus({ id, status: newStatus });
      if (res?.status === 200) {
        toast.success(res.data.message);
        setRefresh(!refresh);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setStatusDialog({ open: false, id: "", currentStatus: 0 });
    }
  };

  const handleBlockUnblock = (id, isBlocked) => {
    setBlockDialog({ open: true, id, isBlocked });
  };

  const confirmBlockUnblock = async () => {
    const { id, isBlocked } = blockDialog;
    try {
      const res = await userServices.blockUnblockUser({ id, isBlocked });
      if (res?.status === 200) {
        toast.success(res.data.message);
        setRefresh(!refresh);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setBlockDialog({ open: false, id: "", isBlocked: false });
    }
  };

  const archiveUser = async (id) => {
    const result = await Swal.fire({
      text: "Are you sure you want to archive this user?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Archive",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ff9800",
    });

    if (result.isConfirmed) {
      try {
        const res = await userServices.changeStatus({ id, status: 3 });
        if (res?.status === 200) {
          toast.success(res.data.message || "User archived successfully");
          setRefresh(!refresh);
        } else {
          toast.error(res.data.message || "Failed to archive user");
        }
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const unarchiveUser = async (id) => {
    const result = await Swal.fire({
      text: "Are you sure you want to unarchive this user?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Unarchive",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#28a745",
    });

    if (result.isConfirmed) {
      try {
        const res = await userServices.changeStatus({ id, status: 1 });
        if (res?.status === 200) {
          toast.success(res.data.message || "User unarchived successfully");
          setRefresh(!refresh);
        } else {
          toast.error(res.data.message || "Failed to unarchive user");
        }
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const handleKycStatusChange = async (userId, newStatus) => {
    try {
      const res = await userServices.kycStatus({ id: userId, kycStatus: newStatus });
      if (res?.status === 200) {
        toast.success(res.data.message || "KYC status updated successfully");
        setRefresh(!refresh);
      } else {
        toast.error(res.data.message || "Failed to update KYC status");
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleToggleArchivedView = () => {
    setIsArchivedView(!isArchivedView);
    setCurrentPage(1);
    setSearchItem("");
    setStatus([]);
    setRole([]);
    setKycStatus([]);
    setAccountType([]);
  };

  useEffect(() => {
    fetchUsers(searchItem);
  }, [currentPage, pageSize, status, refresh, role, kycStatus, accountType, isArchivedView]);

  const columns = [
    getCheckboxColumn(),
    {
      name: "S.No.",
      width: "80px",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      name: "User ID",
      selector: (row) =>
        (row.userId),
      sortable: false,
    },
    {
      name: "Name",
      selector: (row) =>
        capitalize(row.firstName + " " + row.lastName),
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Phone Number",
      selector: (row) => row.phoneNumber,
      sortable: true,
    },
    {
      name: "Account Type",
      selector: (row) =>
        row.role === "consumer"
          ? "Consumer"
          : row.role === "dealer"
            ? "Dealer"
            : capitalize(row.role),
      sortable: false,
    },
    {
      name: "KYC Status",
      cell: (row) => {
        const currentStatus = row.kycStatus || "not_submitted";
        return (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={currentStatus}
              onChange={(e) => {
                const newStatus = e.target.value;
                if (newStatus !== currentStatus) {
                  handleKycStatusChange(row._id, newStatus);
                }
              }}
              sx={{
                fontSize: "0.875rem",
                "& .MuiSelect-select": {
                  py: 1,
                },
              }}
            >
              <MenuItem value="not_submitted">Not Submitted</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        );
      },
      sortable: false,
      width: "180px",
    },
    ...(!isArchivedView ? [{
      name: "Status",
      cell: (row) => {
        const isBlocked = row.isBlocked || false;
        const isActive =
          row.status !== undefined ? row.status : row.status === 1;
        return (
          <Switch
            checked={isActive && !isBlocked}
            onClick={() => {
              if (isBlocked) {
                handleBlockUnblock(row._id, false);
              } else {
                setStatusDialog({
                  open: true,
                  id: row._id,
                  currentStatus: isActive ? 1 : 0,
                });
              }
            }}
          />
        );
      },
      width: "100px",
    }] : []),
    {
      name: "Action",
      center: true,
      width: isArchivedView ? "250px" : "220px",
      cell: (row) => (
        <Box display="flex" gap={1} justifyContent="center">
          <Tooltip title="View Details"
            placement="top"
            arrow
            slotProps={{
              transition: {
                timeout: 200, 
                style: { transformOrigin: "center bottom" } 
              }
            }}>
            <IconButton
              onClick={() => {
                navigate(`/user-management/view/${row._id}`);
              }}
              sx={{ color: "primary.main" }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          {!isArchivedView && (
            <>
              <Tooltip title="Edit" placement="top" disableInteractive arrow>
                <IconButton
                  onClick={() => navigate(`/user-management/edit/${row._id}`)}
                  sx={{ color: "primary.main" }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Archive" placement="top" arrow>
                <IconButton
                  onClick={() => archiveUser(row._id)}
                  sx={{ color: "warning.main" }}
                >
                  <ArchiveIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          {isArchivedView && (
            <>
              <Tooltip title="Delete">
                <IconButton
                  onClick={() => deleteUser(row._id)}
                  sx={{ color: "error.main" }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Unarchive" placement="top" arrow>
                <IconButton
                  onClick={() => unarchiveUser(row._id)}
                  sx={{ color: "success.main" }}
                >
                  <UnarchiveIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader title={isArchivedView ? "Archived Users" : "User Management"} />
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="flex-end"
        alignItems="center"
        p={2}
        gap={2}
      >
        <TableFilter
          data={ACCOUNT_TYPE_OPTIONS}
          value={accountType}
          setValue={setAccountType}
          filterName="Account Type"
        />
        <TableFilter
          data={KYC_STATUS_OPTIONS}
          value={kycStatus}
          setValue={setKycStatus}
          filterName="KYC Status"
        />
        <TableFilter
          data={STATUS_OPTIONS_LIST}
          value={status}
          setValue={setStatus}
          filterName="Status"
        />
        <Input
          value={searchItem}
          placeholder="Search"
          onChange={handleSearchChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  setSearchItem("");
                  fetchUsers("");
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
        {selectedRows.length > 0 && !isArchivedView && (
          <Button
            variant="contained"
            color="warning"
            onClick={handleBulkArchive}
            startIcon={<ArchiveIcon />}
          >
            Archive Selected ({selectedRows.length})
          </Button>
        )}
        {selectedRows.length > 0 && isArchivedView && (
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            startIcon={<DeleteIcon />}
          >
            Delete Selected ({selectedRows.length})
          </Button>
        )}
        <Button
          variant={isArchivedView ? "outlined" : "contained"}
          onClick={handleToggleArchivedView}
          // startIcon={<ArchiveIcon />}
        >
          {isArchivedView ? "Active Users" : "Archived Users"}
        </Button>
        {!isArchivedView && (
          <Button
            variant="contained"
            onClick={() => navigate("/user-management/add")}
          >
            Add User
          </Button>
        )}
      </Box>

      <CardContent>
        <DataTable
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          onChangeRowsPerPage={(rows) => setPageSize(rows)}
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationComponent={(props) => (
            <CustomPagination {...props} currentPage={currentPage} />
          )}
          customStyles={{
            headCells: {
              style: { fontWeight: "bold", fontSize: "14px" },
            },
          }}
        />
      </CardContent>

      <Dialog
        open={statusDialog.open}
        onClose={() =>
          setStatusDialog({ open: false, id: "", currentStatus: 0 })
        }
      >
        <DialogTitle>
          Are you sure you want to{" "}
          {statusDialog.currentStatus === 1 ? "deactivate" : "activate"} this
          user?
        </DialogTitle>
        <DialogActions>
          <Button variant="outlined" onClick={toggleStatus} color="primary">
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={() =>
              setStatusDialog({ open: false, id: "", currentStatus: 0 })
            }
          >
            No
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={blockDialog.open}
        onClose={() =>
          setBlockDialog({ open: false, id: "", isBlocked: false })
        }
      >
        <DialogTitle>
          Are you sure you want to {blockDialog.isBlocked ? "unblock" : "block"}{" "}
          this user?
        </DialogTitle>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={confirmBlockUnblock}
            color={blockDialog.isBlocked ? "success" : "error"}
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={() =>
              setBlockDialog({ open: false, id: "", isBlocked: false })
            }
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
